import { useContext, useEffect, useState } from "react";
import { Box, Paper } from "@mui/material";
import styles from "./Search.module.scss";
import { useTranslation } from "react-i18next";
import { getLabel } from "../../../misc/utils.functions";
import AvatarList from "../../../components/AvatarList";
import StageComponent from "../StageComponent";
import { PublicationAdvancedFilter } from "../../../misc/pagedFilter";
import {
  ICanStartWorfklow,
  Publication,
  SearchAdvanceFilters,
  WorkflowType,
} from "../../../models/workflow.model";
import workflowService from "../../../services/workflow.service";
import moment from "moment";
import { PaginatedDataTable } from "../../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import BackButton from "../../../components/BackButton";
import { useNavigate } from "react-router";
import SearchForm from "./SearchForm";
import useServiceReducer from "../../../hooks/useServiceReducer";
import searchReducer from "./searchReducer";
import { useSessionStorageWithObject } from "../../../hooks/useSessionStorage";
import StatusComponent from "../StatusComponent";
import SearchAdvancedForm from "./SearchAdvancedForm";
import ActionsMenu from "./ActionsMenu";
import ModifyDialog from "../Modify/ModifyDialog";
import WorkflowTypeComponent from "./WorkflowType";
import CancelWorkflowDialog from "../CancelWorkflowDialog";
import MainContext from "../../../context/mainContext";
import { RoleCode } from "../../../models/user.model";
import RepublishWarningDialog from "../RepublishWarningDialog";

interface Props {
  title: string;
  status?: string;
  sortOrder?: string;
  sortDescending?: boolean;
}

const SearchResult: React.FC<Props> = ({
  title,
  status,
  sortOrder,
  sortDescending,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, service } = useContext(MainContext);

  const isCommunicationTeam = currentUser?.roles.includes(
    RoleCode.Communications
  );

  const [canRepublishChange, setCanRepublishChange] =
    useState<ICanStartWorfklow>();

  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [modifySelected, setModifySelected] = useState<Publication>();
  const [modifyType, setModifyType] = useState<WorkflowType>("Modification");
  const [cancelDialogShow, setCancelDialogShow] = useState(false);
  const [currentPublication, setCurrentPublication] =
    useState<Publication | null>(null);

  const initFilter: PublicationAdvancedFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortField: sortOrder ?? "title",
    sortDescending: sortDescending ?? false,
    search: "",
    criteria: 0,
    status: status ?? "",
    topics: [],
    filterBy: "byDivision",
    divisions: [],
    departments: [],
    countries: [],
    languages: [],
    fieldDate: null,
    fromDate: null,
    toDate: null,
  };

  const [currentFilter, setCurrentFilter] =
    useSessionStorageWithObject<PublicationAdvancedFilter>(
      `search_filters`,
      initFilter
    );

  const [data, loading, filter, dispatch, loader, reload] = useServiceReducer(
    workflowService.search,
    searchReducer,
    currentFilter
  );

  useEffect(() => {
    setCurrentFilter(filter);
  }, [filter]);

  const handleContinueAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      navigate(`/workflow/${publication.ticketNumber}`);
    }
  };

  const handleViewSummaryAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      navigate(`/summary/${publication.ticketNumber}`);
    }
  };

  const handleMinorChangesAction = (publication: Publication | undefined) => {
    if (publication !== undefined) {
      service(
        () => workflowService.getCanModifiyWorkflow(publication.ticketNumber),
        {
          callback: (data) => {
            if (data?.canStart) {
              setCanRepublishChange(undefined);
              navigate(`/republish/${publication.ticketNumber}`);
            } else {
              setCanRepublishChange(data);
            }
          },
          loader: loader,
        }
      );
    }
  };

  const cancelPublication = () => {
    setCancelDialogShow(false);
    if (currentPublication !== null) {
      workflowService
        .cancelPublication(currentPublication.ticketNumber)
        .then(() => {
          setCurrentPublication(null);
          dispatch({
            type: "SEARCH",
            value: {
              ...currentFilter,
              pageNumber: 1,
            },
          });
        });
    }
  };

  const handleCancelAction = (publication: Publication) => {
    setCancelDialogShow(true);
    setCurrentPublication(publication);
  };

  const isInProgressSearch = status === "InProgress";

  const baseColumns: GridColDef[] = [
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: true,
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <p className={styles.searchTitleCell}>
            <WorkflowTypeComponent type={params.row.workflowType} />{" "}
            {params.row.title}
          </p>
        );
      },
    },
    {
      field: "topic",
      headerName: getLabel("topic"),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "authors",
      headerName: getLabel("authorsCoAuthors"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        return <AvatarList users={params.row.authors} maxAvatars={3} />;
      },
    },
    {
      field: isInProgressSearch ? "startedDate" : "publishedDate",
      headerName: isInProgressSearch
        ? getLabel("startedDate")
        : getLabel("publicationDate"),
      sortable: true,
      width: 120,
      renderCell: (params) => {
        if (params?.row.status === "Cancelled") {
          return getLabel("statusCodes.na");
        } else {
          switch (params.row.workflowType) {
            case "Creation":
            case "Modification": {
              if (params?.row.publishedDate != null) {
                return moment(params?.row.publishedDate).format("MM/DD/YYYY");
              } else {
                return getLabel("pending");
              }
            }
            case "Delete":
              if (params?.row.status === "Deleted") {
                return moment(params?.row.updatedDate).format("MM/DD/YYYY");
              } else {
                return getLabel("pending");
              }
            default:
              return "";
          }
        }
      },
      valueFormatter: (params) => {
        if (params?.value != null) {
          return moment(params?.value).format("MM/DD/YYYY");
        } else {
          return getLabel("pending");
        }
      },
    },
  ];

  useEffect(() => {
    let calculated = [...baseColumns];

    if (status === undefined) {
      calculated.push({
        field: "status",
        headerName: getLabel("status"),
        sortable: false,
        width: 120,
        renderCell: (params) => {
          return <StatusComponent statusCode={params.row.status} />;
        },
      });
    }

    if (isInProgressSearch) {
      calculated.push({
        field: "stage",
        headerName: getLabel("stage"),
        sortable: false,
        width: 120,
        renderCell: (params) => {
          return (
            <StageComponent
              workflowType={params.row.workflowType}
              stageCode={params.row.stageCode}
              stageStep={params.row.stageStep}
              isCompleted={params.row.isCompleted}
              waitingApproval={params.row.waitingApproval}
            />
          );
        },
      });
    }

    calculated.push({
      field: "actions",
      headerName: getLabel("actions"),
      sortable: false,
      width: 60,

      renderCell: (params) => {
        return (
          <ActionsMenu
            status={params.row.status}
            isOwner={params.row.isOwner}
            modifyAction={() => handleModifyAction(params.row, "Modification")}
            deleteAction={() => handleModifyAction(params.row, "Delete")}
            continueAction={() => handleContinueAction(params.row)}
            viewSummaryAction={() => handleViewSummaryAction(params.row)}
            cancelAction={() => handleCancelAction(params.row)}
            minorChangesAction={
              isCommunicationTeam
                ? () => handleMinorChangesAction(params.row)
                : undefined
            }
          />
        );
      },
    });

    setColumns(calculated);
  }, [status]);

  const handlePageChange = (pageNumber: number) => {
    dispatch({ type: "PAGE", value: pageNumber });
  };

  const handleSortChange = (field: string, sortDescending: boolean) => {
    dispatch({
      type: "SEARCH",
      value: {
        ...currentFilter,
        sortField: field,
        sortDescending: sortDescending,
      },
    });
  };

  const handleFilters = (filters: SearchAdvanceFilters) => {
    dispatch({
      type: "SEARCH",
      value: {
        ...currentFilter,
        pageNumber: 1,
        countries: filters.countries,
        filterBy: filters.filterBy,
        divisions: filters.divisions,
        departments: filters.departments,
        languages: filters.languages,
        topics: filters.topics,
        fieldDate: filters.fieldDate,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    });
  };

  const handleRowClick = (row: Publication) => {
    if (row.status === "Published") {
      navigate(`/publications/${row.ticketNumber}`);
    } else {
      navigate(`/summary/${row.ticketNumber}`);
    }
  };

  const handleSearch = (search: string, criteria: Number) => {
    dispatch({
      type: "SEARCH",
      value: {
        ...currentFilter,
        pageNumber: 1,
        search: search,
        criteria: criteria,
      },
    });
  };

  const changeCriteria = (search: string, criteria: Number) => {
    dispatch({
      type: "SEARCH",
      value: {
        ...currentFilter,
        pageNumber: 1,
        search: search,
        criteria: criteria,
      },
    });
  };

  const handleModifyAction = (
    publication: Publication | undefined,
    type: WorkflowType
  ) => {
    setModifyType(type);
    setModifySelected(publication);
  };

  const handleModifyCancel = () => {
    setModifyType("Modification");
    setModifySelected(undefined);
  };

  return (
    <Box className={styles.main}>
      <Paper elevation={3} className={styles.basic}>
        <div className={styles.advanceFilters}>
          <div className={styles.backContainer}>
            <BackButton text="back" to="/" />
          </div>
          <SearchAdvancedForm
            onFilter={handleFilters}
            currentFilters={currentFilter}
            readonly={
              currentFilter.criteria === 1 || currentFilter.criteria === 2
            }
          />
        </div>
        <div className={styles.searchResults}>
          <div className={styles.searchRow}>
            <SearchForm
              handleSearch={handleSearch}
              changeCriteria={changeCriteria}
              searchDefault={currentFilter.search}
              criteriaDefault={currentFilter.criteria}
            />
          </div>
          <div className={styles.header}>
            <h3>{title}</h3>
            <div className={styles.workflowTypes}>
              <p>
                <WorkflowTypeComponent type="Creation" />
                {t("creation")}
              </p>
              <p>
                <WorkflowTypeComponent type="Modification" />
                {t("modification")}
              </p>
              <p>
                <WorkflowTypeComponent type="Delete" />
                {t("delete")}
              </p>
            </div>
          </div>
          <PaginatedDataTable<Publication>
            rows={data ? data.list : []}
            totalRows={data ? data.totalRows : 0}
            initialPage={currentFilter.pageNumber - 1}
            pageSize={currentFilter.pageSize}
            columns={columns}
            handlePageChange={handlePageChange}
            handleRowClick={handleRowClick}
            handleSortChange={handleSortChange}
            emptyMessage={t("noRowsMessage", {
              entity: t("publications"),
            })}
            loading={loading}
          />
        </div>
      </Paper>
      {modifySelected && (
        <ModifyDialog
          ticketNumber={modifySelected.ticketNumber}
          cancelAction={handleModifyCancel}
          open={Boolean(modifySelected)}
          type={modifyType}
        />
      )}
      {canRepublishChange && !canRepublishChange.canStart && (
        <RepublishWarningDialog
          mostRecentTicketNumber={canRepublishChange.mostRecentTicketNumber}
          open={true}
          close={() => {
            setCanRepublishChange(undefined);
          }}
        />
      )}
      <CancelWorkflowDialog
        open={cancelDialogShow}
        yesAction={() => cancelPublication()}
        noAction={() => setCancelDialogShow(false)}
      />
    </Box>
  );
};

export default SearchResult;
